import React, {useCallback, useRef} from 'react';
import {useDropzone} from 'react-dropzone';
import PropTypes from 'prop-types';
import css from './FileUpload.module.scss';
import ImageAdd from "../../Icons/ImageAdd.jsx";
import Trash01 from "../../Icons/Trash01.jsx";
import Button from "../../Button/Button.jsx";
import EyeOpen from "../../Icons/EyeOpen.jsx";
import FlexContainer from "../../FlexContainer/FlexContainer.jsx";
import {getFileExtension} from "../../../utils/fileConverters.js";

const FileUpload = ({
  onFilesChange,
  files = [],
  label,
  required = false,
  maxFiles = 10,
  error,
  onFileDelete,
  fileClassName,
  accept,
  onFilesRejected,
  disabled = false,
  icon,
  supportText,
  fullWidth = false,
}) => {
  const fileInputRef = useRef(null);
  const inputAccept = [...new Set([...Object.keys(accept), ...Object.values(accept).flat()])].join(',');

  const handleFiles = useCallback((selectedFiles, rejectedFiles = []) => {
    const acceptedExtensions = Object.values(accept).flat().map(extension => extension.toLowerCase());
    const isAccepted = (file) => (
      Object.hasOwn(accept, file.type) || acceptedExtensions.includes(`.${getFileExtension(file.name)}`)
    );

    const candidates = Array.from(selectedFiles);
    const rejected = [...rejectedFiles, ...candidates.filter(file => !isAccepted(file))];
    if (rejected.length > 0) {
      onFilesRejected?.(rejected);
    }

    const acceptedFiles = candidates.filter(isAccepted);
    if (acceptedFiles.length === 0) {
      return;
    }

    const newFiles = acceptedFiles
      .map(file => {
        let previewUrl = URL.createObjectURL(file);

        return {
          file: file,
          previewUrl: previewUrl,
        };
      });

    const updatedFiles = maxFiles === 1
      ? newFiles.slice(0, 1)
      : [...files, ...newFiles].slice(0, maxFiles);

    onFilesChange?.(updatedFiles);
  }, [onFilesChange, onFilesRejected, maxFiles, files, accept]);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    handleFiles(acceptedFiles, fileRejections.map(rejection => rejection.file));
  }, [handleFiles]);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    fileInputRef.current?.click();
  }, [disabled]);

  const handleFileInputChange = useCallback((e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  }, [handleFiles]);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept,
    noClick: true,
    disabled,
  });

  const deleteFile = async (index) => {
    try {
      const newFiles = [...files];
      if (newFiles[index] && newFiles[index].id) {
        await onFileDelete(newFiles[index].id)
      }
      const [removedFile] = newFiles.splice(index, 1);
      if (removedFile?.file) {
        URL.revokeObjectURL(removedFile.previewUrl);
      }
      onFilesChange?.(newFiles);
    } catch (error) {
      console.log(error);
    }
  };

  const openFile = (fileUrl) => {
    window.open(fileUrl)
  }

  const supportedFileTypes = [...new Set(Object.values(accept).flat())].join(', ');

  return (
    <div className={css.fileUpload}>
      {label &&
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      }
      <div className={css.uploaderContainer}>
        {files.map((file, index) => {
          return (
            <div key={index} className={`${css.previewItem} ${fileClassName}`}>
              <Button variant="ghost" size="small" onClick={() => openFile(file.previewUrl)}>
                <EyeOpen/> View
              </Button>
              <button
                type="button"
                className={css.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteFile(index);
                }}
              >
                <Trash01 color="var(--danger)" width={14}/>
              </button>
              {file.file && <FlexContainer>{file.file.name}</FlexContainer>}
            </div>
          )
        })}
        <div
          {...getRootProps()}
          className={`${css.dropzone} ${fileClassName} ${isDragActive ? css.active : ''} ${error ? css.error : ''} ${disabled ? css.disabled : ''} ${fullWidth ? css.fullWidth : ''}`}
          onClick={handleClick}
          data-error={!!error}
          aria-disabled={disabled}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple={maxFiles > 1}
            accept={inputAccept}
            onChange={handleFileInputChange}
            disabled={disabled}
            style={{display: 'none'}}
            required={required && files.length === 0}
          />
          {isDragActive ?
            <p>Drop files here...</p>
            :
            <div className={css.content}>
              {icon ?? <ImageAdd width={20} color="var(--primary-color)"/>}
              <div className={css.uploadText}>
                <span className={css.clickText}>Click to upload</span> or drag and drop
                <div className={css.supportText}>{supportText ?? `Supported files: ${supportedFileTypes}`}</div>
              </div>
            </div>
          }
        </div>
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

FileUpload.propTypes = {
  onFilesChange: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.string
  ),
  label: PropTypes.string,
  required: PropTypes.bool,
  maxFiles: PropTypes.number,
  error: PropTypes.string,
  accept: PropTypes.object.isRequired,
  onFilesRejected: PropTypes.func,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  supportText: PropTypes.node,
  fullWidth: PropTypes.bool,
};

export default FileUpload;
